import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatValue } from "./format-value";

export interface PendingChange {
  diff: { path: string; before: unknown; after: unknown }[];
  /** Extra warning shown above the table, for destructive or propagating actions. */
  warning?: string;
  confirmLabel?: string;
}

/**
 * Confirm a write by showing exactly what it will change.
 *
 * The legacy tool used `window.confirm("Are you sure?")`, which tells an operator
 * nothing about what they are about to do to a paying customer's access. Every write
 * in this section goes through this dialog instead.
 */
export function SaveDiffDialog({
  pending,
  saving,
  onCancel,
  onConfirm,
}: {
  pending: PendingChange | null;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={pending !== null} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Review this change</DialogTitle>
          <DialogDescription>
            It applies to the live External API. Both runtimes have their cached login
            evicted, the change is recorded, and an email goes to the configured recipients.
          </DialogDescription>
        </DialogHeader>

        {pending?.warning && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
            {pending.warning}
          </p>
        )}

        {pending && pending.diff.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing has changed.</p>
        ) : (
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="w-1/4 pb-1 text-left font-medium">Field</th>
                  <th className="pb-1 text-left font-medium">Before</th>
                  <th className="pb-1 text-left font-medium">After</th>
                </tr>
              </thead>
              <tbody className="align-top">
                {pending?.diff.map((entry) => (
                  <tr key={entry.path} className="border-t">
                    <td className="py-1.5 pr-2">
                      <code className="text-xs">{entry.path}</code>
                    </td>
                    <td className="py-1.5 pr-2 text-muted-foreground">
                      {formatValue(entry.before)}
                    </td>
                    <td className="py-1.5">{formatValue(entry.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={saving || pending?.diff.length === 0}>
            {saving ? "Saving…" : (pending?.confirmLabel ?? "Apply change")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
